import { useState, useEffect, useRef, useCallback } from "react";
import * as axios from "axios";
import { ArticleExtraData } from "articlePage/stateInterfaces";
import { TickerViewData } from "../../../../../tickerAuto/interfaces";
import { addAxiosDateTransformerSettings } from "../../../../../../../utils/network/axiosDateTransformer";
import { ItemJsonVer2 } from "../../../../../../jsonSerializer/widgetJsonInterfacesVer2";
import { mapItemJsonToArticleExtraData } from "../utils/newsFlashUtils";
import Slider from "react-slick";
import { isWcmEditor } from "../../../../../../../config/serverConfig";
import { NewsFlashSpecialDesktopSlotItem } from "./newsFlashSpecialDesktopSlotItem";
import { NewsFlashSpecialDesktopPopup } from "./newsFlashSpecialDesktopPopup";
import { HyperLinkBuilder } from "../../../../../../commonComponents/inline/hyperLink";
import { LinkTargetProperties } from "../../../../../../../global/components/linkSettings/interfaces";
require("./ynet_site_newsFlashSpecialDesktopComponent.less")

interface TickerAutoRowViewProps {
    domain: string
    componentaId: string
    languageCode: string
    showScrollArrows: boolean
    bgHighlightDuration: number
    numberOfVisibleItems: number
    data: Partial<TickerViewData>
    popupNumberOfVisibleItems: number
    isLtr?: boolean
}

export const NewsFlashSpecialDesktopComponent = (props: TickerAutoRowViewProps) => {

    const { data, componentaId, domain, isLtr = false, languageCode, numberOfVisibleItems, popupNumberOfVisibleItems, showScrollArrows, bgHighlightDuration } = props
    const [itemList, setItemList] = useState<ArticleExtraData[]>([]);
    const [isFirstInterval, setIsFirstInterval] = useState(true);
    const [shouldDisplayLoader, setShouldDisplayLoader] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [openedItemId, setOpenedItemId] = useState<string | null>(null);
    const [shouldCarouselAutoplay, setShouldCarouselAutoplay] = useState(true);
    const [highlightedArticleId, setHighlightedArticleId] = useState<string | null>(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [bellPhase, setBellPhase] = useState<"static" | "dots" | "bell">("bell");
    const [newItemIds, setNewItemIds] = useState<string[]>([]);
    const [isSliderReady, setIsSliderReady] = useState(false);
    const isFirstIntervalRef: any = useRef(true);
    const sliderRef: any = useRef(null);
    const popupRef: any = useRef(null);
    const bellTimersRef: any = useRef<{ dots?: ReturnType<typeof setTimeout>; bell?: ReturnType<typeof setTimeout> }>({});
    const prevItemKeysRef: any = useRef<string[]>([]);
    const newItemTimerRef: any = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isPopupOpenRef: any = useRef(false);
    const itemListRef: any = useRef<ArticleExtraData[]>([]);
    const gateDoneRef: any = useRef(false);
    const gateMoveCountRef: any = useRef(0);
    const gatePrevTrackXRef: any = useRef<number | null>(null);
    const gateTimeoutRef: any = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isEditor = isWcmEditor();

    const parseTrackTranslateX = (transformValue: string) => {
        if (!transformValue) return 0;

        const translate3dMatch = transformValue.match(/translate3d\(([\-0-9.]+)px,\s*([\-0-9.]+)px,\s*([\-0-9.]+)px\)/);
        if (translate3dMatch && translate3dMatch[1]) {
            return Number(translate3dMatch[1]);
        }

        const translateXMatch = transformValue.match(/translateX\(([\-0-9.]+)px\)/);
        if (translateXMatch && translateXMatch[1]) {
            return Number(translateXMatch[1]);
        }

        return 0;
    };

    useEffect(() => {
        isPopupOpenRef.current = isPopupOpen;
    }, [isPopupOpen]);

    useEffect(() => {
        itemListRef.current = itemList;
    }, [itemList]);

    useEffect(() => {
        if (isInitialLoad && itemList.length > 0) {
            setIsInitialLoad(false);
        }
    }, [itemList]);

    useEffect(() => {
        if (itemList.length === 0) {
            setIsSliderReady(false);
            gateDoneRef.current = false;
            gateMoveCountRef.current = 0;
            gatePrevTrackXRef.current = null;
            if (gateTimeoutRef.current) {
                clearTimeout(gateTimeoutRef.current);
                gateTimeoutRef.current = null;
            }
            return;
        }

        if (gateDoneRef.current) {
            setIsSliderReady(true);
            return;
        }

        const unlockSlider = (reason: string) => {
            if (gateDoneRef.current) return;

            gateDoneRef.current = true;
            setIsSliderReady(true);
        };

        let track: HTMLElement = null;
        if (sliderRef.current && sliderRef.current.innerSlider && sliderRef.current.innerSlider.list) {
            const slickList = sliderRef.current.innerSlider.list as HTMLElement;
            track = slickList.querySelector(".slick-track") as HTMLElement;
        }

        if (!track) {
            gateTimeoutRef.current = setTimeout(() => unlockSlider("no-track-timeout"), 300);
            return () => {
                if (gateTimeoutRef.current) {
                    clearTimeout(gateTimeoutRef.current);
                    gateTimeoutRef.current = null;
                }
            };
        }

        setIsSliderReady(false);

        const onTrackMutation = () => {
            const x = parseTrackTranslateX(track.style.transform || "");
            const prevX = gatePrevTrackXRef.current;

            if (prevX !== null && Math.abs(x - prevX) >= 60) {
                gateMoveCountRef.current += 1;

                if (gateMoveCountRef.current >= 2) {
                    unlockSlider("second-track-shift");
                }
            }

            gatePrevTrackXRef.current = x;
        };

        onTrackMutation();
        const observer = new MutationObserver(onTrackMutation);
        observer.observe(track, {
            attributes: true,
            attributeFilter: ["style", "class"],
        });

        gateTimeoutRef.current = setTimeout(() => unlockSlider("timeout"), 650);

        return () => {
            observer.disconnect();

            if (gateTimeoutRef.current) {
                clearTimeout(gateTimeoutRef.current);
                gateTimeoutRef.current = null;
            }
        };
    }, [itemList.length]);

    const nowDuration = data.highlightingDuration ? data.highlightingDuration : 0;
    const mainClassName = `${isLtr ? "newsflash-special-desktop-eng-component" : "newsflash-special-desktop-component"} ${isPopupOpen ? "popup-open" : ""}`;
    const wrappedImageClassNames = `newsflash-special-desktop-slot-list animationDiv ${isEditor ? "isEditor" : "tickerAnimation"} ${isSliderReady ? "" : "newsflash-slider-hidden"}`;

    const seconds = data.seconds || 0;

    const settings = {
        draggable: false,
        infinite: true,
        dots: false,
        variableWidth: true,
        slidesToScroll: 1,
        autoplay: shouldCarouselAutoplay ? seconds > 0 : false,
        autoplaySpeed: seconds * 1000,
        pauseOnHover: true,
        arrows: showScrollArrows,
        rtl: !isLtr,
        centerMode: false,
        beforeChange: (current: number, next: number) => {
            // setOpenedItemId(null);
        },
        initialSlide: isLtr ? 0 : 3,
        nextArrow: <SampleArrow />,
        prevArrow: <SampleArrow />,
    };

    const getArticleKey = useCallback((article: ArticleExtraData) => `${article.articleId}_${new Date(article.dateUpdatedOnSite).getTime()}`, []);

    const sortByUpdatedDateDesc = useCallback((items: ArticleExtraData[]) => {
        return items.slice().sort((a, b) => {
            const aTime = new Date(a.dateUpdatedOnSite).getTime();
            const bTime = new Date(b.dateUpdatedOnSite).getTime();
            return bTime - aTime;
        });
    }, []);

    const mergeMissingNewItems = useCallback((fetchedItems: ArticleExtraData[], prevList: ArticleExtraData[]) => {
        if (prevList.length === 0) {
            return fetchedItems;
        }

        const fetchedKeys = fetchedItems.map(item => getArticleKey(item));
        const missingItems = prevList.filter(item => !fetchedKeys.includes(getArticleKey(item)));

        if (missingItems.length === 0) {
            return fetchedItems;
        }

        return sortByUpdatedDateDesc(missingItems.concat(fetchedItems));
    }, [getArticleKey, sortByUpdatedDateDesc]);

    const getTickerArticles = useCallback(async () => {
        const axiosInstance = axios.create(addAxiosDateTransformerSettings());
        const isPopupCurrentlyOpen = isPopupOpenRef.current;
        const currentItemList = itemListRef.current;

        try {
            if (isPopupCurrentlyOpen) {
                // Fetch from mobile popup API
                const res: any = await axiosInstance.get("/iphone/json/mobile/v2/page/184");
                const newArticles: ItemJsonVer2[] = res.data.components.find((component: any) => component.name === "flashesHeadlines").items;
                const converted = newArticles.map(article => mapItemJsonToArticleExtraData(article)).slice(0, 100);
                const finalList = mergeMissingNewItems(converted, currentItemList);

                const newKeys = converted.map(item => getArticleKey(item));
                const hasNewArticle = prevItemKeysRef.current.length > 0 &&
                    newKeys.some(key => !prevItemKeysRef.current.includes(key));

                if (hasNewArticle) {
                    const arrivedIds = converted
                        .filter(a => !prevItemKeysRef.current.includes(getArticleKey(a)))
                        .map(a => a.articleId);
                    setNewItemIds(arrivedIds);

                    if (newItemTimerRef.current) clearTimeout(newItemTimerRef.current);
                    newItemTimerRef.current = setTimeout(() => setNewItemIds([]), (bgHighlightDuration) * 1000);

                    setShouldDisplayLoader(true);
                    setTimeout(() => setShouldDisplayLoader(false), 2000);
                }

                prevItemKeysRef.current = newKeys;
                setItemList(finalList);
            } else {
                // Fetch from auto ticker API
                const res: any = await axiosInstance.get(`/iphone/json/api/auto_ticker/${componentaId}`);
                const newArticles = res.data.data.tickerAutoDesktop.data.slice(0, 10);
                const converted: ArticleExtraData[] = newArticles;
                const finalList = mergeMissingNewItems(converted, currentItemList);

                const newKeys = converted.map(item => getArticleKey(item));
                const hasNewArticle = prevItemKeysRef.current.length > 0 &&
                    newKeys.some(key => !prevItemKeysRef.current.includes(key));

                if (hasNewArticle) {
                    const arrivedIds = converted
                        .filter(a => !prevItemKeysRef.current.includes(getArticleKey(a)))
                        .map(a => a.articleId);
                    setNewItemIds(arrivedIds);

                    if (newItemTimerRef.current) clearTimeout(newItemTimerRef.current);
                    newItemTimerRef.current = setTimeout(() => setNewItemIds([]), (bgHighlightDuration) * 1000);

                    setShouldDisplayLoader(true);
                    setTimeout(() => setShouldDisplayLoader(false), 2000);
                }

                prevItemKeysRef.current = newKeys;
                setItemList(finalList);
            }
        } catch (err) {
            console.error("error getting ticker data:", err);
        }
    }, [componentaId, bgHighlightDuration, getArticleKey, mergeMissingNewItems]);


    useEffect(() => {
        if (data.category && data.category.link.id) {
            getTickerArticles();

            const intervalId = setInterval(() => {
                isFirstIntervalRef.current = false;
                setIsFirstInterval(false);
                getTickerArticles();
            }, 60000);

            return () => clearInterval(intervalId);
        }
    }, [data.category.link.id, getTickerArticles]);

    useEffect(() => {
        if (isPopupOpen) {
            getTickerArticles();
        }
    }, [isPopupOpen, getTickerArticles]);

    const dispatchCustomEvent = (articleId: string) => {
        const openIndex = itemList.findIndex(item => item.articleId === articleId);
        const itemTitle = itemList[openIndex] && itemList[openIndex].title;

        document.dispatchEvent(
            new CustomEvent("newsFlashesReadMoreChange", {
                detail: {
                    action: articleId === openedItemId ? "Collapse" : "Expand",
                    label: itemTitle
                }
            })
        );
    };

    const handleItemToggle = (articleId: string) => {
        setOpenedItemId(openedItemId !== articleId ? articleId : null);
        dispatchCustomEvent(articleId);
        handleOpenPopup(articleId);
    };

    const handleCarouselAutoplay = (shouldAutoplay: boolean) => {
        setShouldCarouselAutoplay(shouldAutoplay);
    };

    const handleOpenPopup = (articleId?: string) => {
        if (isWcmEditor()) return;

        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        document.body.style.overflow = "hidden";

        setHighlightedArticleId(articleId || null);
        setIsPopupOpen(true);
    };

    const handleClosePopup = () => {
        document.body.style.paddingRight = "";
        document.body.style.overflow = "";
        setIsPopupOpen(false);
    };

    const startBellSequence = (withDots: boolean) => {
        clearTimeout(bellTimersRef.current.dots);
        clearTimeout(bellTimersRef.current.bell);

        if (withDots) {
            setBellPhase("dots");
            bellTimersRef.current.dots = setTimeout(() => setBellPhase("bell"), 4000);
            bellTimersRef.current.bell = setTimeout(() => setBellPhase("static"), 8000);
        } else {
            setBellPhase("bell");
            bellTimersRef.current.bell = setTimeout(() => setBellPhase("static"), 4000);
        }
    };

    useEffect(() => {
        if (itemList.length === 0) return;
        if (!isFirstIntervalRef.current) return;

        const hasRecentItem = itemList.some(item => minutesDiff(item) < (nowDuration || 1));
        startBellSequence(hasRecentItem);
    }, [itemList.length > 0]);

    useEffect(() => {
        if (shouldDisplayLoader) {
            startBellSequence(true);
        }
    }, [shouldDisplayLoader]);

    return (
        <div className={`${mainClassName}`}>
            <div className="bell-icon" onClick={() => handleOpenPopup()}>
                {bellPhase === "dots" &&
                    <video src="https://www.ynet.co.il/images/picsrv/3-dots-30x30.webm" className="bell-dots" autoPlay loop muted playsInline />
                }
                {bellPhase === "bell" &&
                    <video src="https://www.ynet.co.il/images/picsrv/Bell-Notification-30x30.webm" className="bell-animated" autoPlay loop muted playsInline />
                }
                {bellPhase === "static" &&
                    <div className="bell-static"></div>
                }
            </div>

            <Slider {...settings} className={wrappedImageClassNames} ref={sliderRef}>
                {itemList.slice(0, numberOfVisibleItems).map(flashItem =>
                    <NewsFlashSpecialDesktopSlotItem
                        isLTR={isLtr}
                        article={flashItem}
                        nowDuration={nowDuration}
                        key={flashItem.articleId}
                        languageCode={languageCode}
                        isInitialLoad={isInitialLoad}
                        isFirstInterval={isFirstInterval}
                        onRowItemToggle={handleItemToggle}
                        bgHighlightDuration={bgHighlightDuration}
                        isNewlyArrived={newItemIds.includes(flashItem.articleId)}
                    />
                )}
            </Slider>

            <HyperLinkBuilder className="more-flashes-btn" href={data.tabHref ? data.tabHref : "#"} linkTarget={data.tabTitleLinkTarget ? data.tabTitleLinkTarget : {} as LinkTargetProperties}>
                <div className="btn-inner-container">
                    <div className="btn-visible-text">עוד</div>
                    <div className="btn-hover-text">מבזקים</div>
                    <div className="arrow-icon"></div>
                </div>
            </HyperLinkBuilder>

            <NewsFlashSpecialDesktopPopup
                isOpen={isPopupOpen}
                onClose={handleClosePopup}
                languageCode={languageCode}
                toAllFlashesLink={data.tabHref}
                hasNewFlash={shouldDisplayLoader}
                highlightingDuration={nowDuration}
                highlightedArticleId={highlightedArticleId}
                toAllFlashesLinkTarget={data.tabTitleLinkTarget}
                bgHighlightDuration={bgHighlightDuration}
                firstArticleItems={itemList.slice(0, popupNumberOfVisibleItems)}
            />
        </div>
    );
};

NewsFlashSpecialDesktopComponent.siteScriptName = "NewsFlashSpecialDesktopComponent";

const SampleArrow = (props: any) => <div className={props.className} onClick={props.onClick} />

export const minutesDiff = (article: ArticleExtraData) => {
    return Math.floor((new Date().getTime() - new Date(article.dateUpdatedOnSite).getTime()) / (1000 * 60));
}